import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dismissal,
  StaffTouch,
  bookingTouches,
  handledAfter,
  hasOptedOut,
  QueueActivity,
  QueueMessage,
  WaitingItem,
  isDismissed,
  mergeWaiting,
  summarise,
  waitingFromActivities,
  waitingFromMessages,
} from './responseQueue'
import { CREATE_FOLLOW_UP_MUTATION, RESPONSE_QUEUE_MESSAGES_QUERY } from './graphql'
import { COHORT_PAGE, readAllPages } from './cohortRead'
import type { Appointment } from 'app/generated/graphql'
import { HOLD_STATES, RESPONSE_QUEUE_NAME, fetchHolds, holdsToDismissals, saveHold } from './queueHolds'
import { SEND_SMS_MUTATION } from 'app/components/contacts/inbox/graphql'
import { gqlFetchList, gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { ActivityJson, ContactJson } from 'app/types/entities'
import { useUser } from 'app/utils'
import { bg } from 'lib/background'
import { logError, logWarn } from 'lib/logging'

/**
 * How far back a request counts as today's work: the start of yesterday.
 *
 * Local day boundaries rather than UTC, because "yesterday and today" is a
 * human idea and the practice reads it in their own timezone. A UTC cut would
 * move the boundary into the middle of somebody's afternoon.
 *
 * Activities only. A missed call from last Tuesday is not this morning's work
 * and nobody is going to clear it from a triage list. Texts are bounded by
 * nothing at all: see the message query below.
 */
function activityWindowStart(): string {
  return moment().subtract(1, `day`).startOf(`day`).toISOString()
}

/**
 * All the older-request count needs, and no more.
 *
 * That query reads a page of records purely to say how many people fell out of
 * the window: none of it is ever drawn, so it stays narrow. The queue's own
 * fetch takes the full model instead, because its rows open onto the real
 * activity cards and those read far more of the record than these rules do.
 *
 * `createdDate` is not read by the queue itself, it stands in for a missing
 * `activityDate`. That field is `Maybe<DateTime>` in the schema and
 * `waitingFromActivities` skips any row without one, so without the fallback a
 * dateless request would vanish rather than be triaged. `useLastTouch` reads
 * the same pair for the same reason.
 */
const RULE_FIELDS = `id type subType tags description activityDate createdDate contacts { id }`

/**
 * Just enough to say who did what, and when.
 *
 * `owner` is the whole point: it is what tells a colleague's work from a row
 * the system wrote. The rest is for the line the queue shows about it.
 */
const STAFF_TOUCH_FIELDS = `id type activityDate createdDate owner { id name } contacts { id }`

/** What `bookingTouches` reads to place a booking in time and on a person. */
const BOOKING_FIELDS = `id patientId createdDate appointmentMadeDate`

/**
 * Requests are matched by type rather than by tag.
 *
 * Tag terms do work against this API (`useAiAgentVoice` filters on
 * `tags.keyword:("Appointment Scheduled" OR ...)` in production), but narrowing
 * to `tags:("Missed Call" OR "Voicemail")` here would be wrong rather than
 * merely cautious: the same fetch has to carry the answers as well as the
 * requests. `waitingFromActivities` clears a person on an outbound `Call`, on a
 * `Connected` `FollowUp`, and on an answered inbound `Call`, and a tag clause
 * would filter all three away, leaving people on the list after somebody had
 * already spoken to them.
 *
 * The tags still do the work, one layer in: `channelForActivity` reads them to
 * tell a missed call from a voicemail, and `isResponse` reads their absence to
 * tell an answered call from one nobody picked up.
 */
const REQUEST_TYPES = `type:(Call OR Form OR Chat OR "Online Scheduler" OR "Virtual Consultation" OR FollowUp)`

/**
 * A waiting person, with the records behind them.
 *
 * `activities` is the touches of `events` as whole activities, newest last. The
 * row summarises them in a line; the expansion draws them as the contact drawer
 * would, which is the only way a voicemail can be played rather than described.
 * Empty for a text, whose thread is fetched live when the row opens.
 */
export type ResponseRow = WaitingItem & {
  contact?: ContactJson
  activities: ActivityJson[]
  /** Set when a colleague already dealt with them after their last message. */
  handled?: StaffTouch
}

/**
 * The contact behind a text row, or a stub carrying only the number and
 * office: the thread is fetched by those two and needs neither a name nor an id.
 */
export function threadParty(row: ResponseRow): ContactJson | undefined {
  if (row.contact) return row.contact
  if (!row.phone) return undefined
  return { id: ``, cellPhone: row.phone, locationId: row.locationId } as ContactJson
}

/**
 * What happened when somebody rang the person back.
 *
 * The queue has to write this record rather than read one. A practice whose
 * calls all arrive on a CallRail tracking number produces no outbound Call
 * activity when the front desk dials back, so a missed call has nothing that
 * could ever clear it and would age out of the window unanswered. Logging the
 * outcome creates the FollowUp the rules already key on, and the practice's own
 * reporting gains the record too.
 */
export type CallOutcome = `connected` | `scheduled` | `left-message` | `no-answer`

function outcomeDescription(outcome: CallOutcome): string {
  if (outcome === `connected`) return `Called back from the Action Center. Connected with them.`
  if (outcome === `scheduled`) return `Called back from the Action Center. Connected and scheduled an appointment.`
  if (outcome === `left-message`) return `Called back from the Action Center. Left a message.`
  return `Called back from the Action Center. No answer, nobody picked up.`
}

/** Scheduled is a connected call that also booked, so it answers the person too. */
function isAnswered(outcome: CallOutcome): boolean {
  return outcome === `connected` || outcome === `scheduled`
}

/** Activities carry contacts as a list; the queue works per contact. */
function toQueueActivities(hits: ActivityJson[]): QueueActivity[] {
  // `id` and `type` are non-null on the model, so a hit missing either is a
  // record the rules cannot read at all rather than one to guess at.
  return hits.flatMap((activity) =>
    activity.id && activity.type
      ? [
          {
            ...activity,
            id: activity.id,
            type: activity.type,
            contactId: activity.contacts?.[0]?.id,
            activityDate: activity.activityDate ?? activity.createdDate,
          },
        ]
      : [],
  )
}

/**
 * Rows taken off the list by hand, held on the API since TRK-5599, so one
 * person's Done clears the row for the whole practice on every machine.
 *
 * Keyed by the row's `id`, so a hold on one of somebody's threads leaves their
 * other rows where they were.
 *
 * The map is the server's answer as of the last poll. The overlay is the few
 * entries this browser has just written: a hold takes effect at the click,
 * the mutation follows in the background, and until a poll confirms it the
 * overlay is what keeps the row from flickering back.
 */
type DismissalMap = Record<string, Dismissal>

/**
 * How long an optimistic entry outranks the server's answer. Long enough for
 * the write and two polls; short enough that a write that silently failed
 * puts the row honestly back on the list.
 */
const OVERLAY_GRACE_MS = 2 * 60 * 1000

export function useResponseQueue(locationTerm?: string) {
  const user = useUser()
  const [rows, setRows] = useState<ResponseRow[]>([])
  const [olderCount, setOlderCount] = useState(0)
  const [olderSummary, setOlderSummary] = useState(``)
  const [loading, setLoading] = useState(true)
  const [cleared, setCleared] = useState<string[]>([])
  const [dismissed, setDismissed] = useState<DismissalMap>({})
  const [overlay, setOverlay] = useState<Record<string, { record: Dismissal; at: number }>>({})
  const generation = useRef(0)

  const remember = useCallback((row: ResponseRow, until?: string) => {
    setOverlay((current) => ({
      ...current,
      [row.id]: { record: { latest: row.latest, until }, at: Date.now() },
    }))
    bg(
      saveHold({
        contactId: row.id,
        queueName: RESPONSE_QUEUE_NAME,
        state: until ? HOLD_STATES.snoozed : HOLD_STATES.done,
        untilDate: until,
      }),
    )
  }, [])

  const activitySince = useMemo(() => activityWindowStart(), [])

  const load = useCallback(() => {
    generation.current += 1
    const token = generation.current
    setLoading(true)
    const scope = locationTerm ? ` AND ${locationTerm}` : ``
    bg(
      Promise.all([
        // Every page: the oldest waits are the threads any page bound would cut off.
        readAllPages(`response queue messages`, async (index) => {
          const result = await gqlQuery(RESPONSE_QUEUE_MESSAGES_QUERY, {
            // No date bound at all, on purpose. A conversation somebody
            // abandoned three weeks ago is still an unanswered person, and
            // unlike a missed call it is still answerable in place, so a window
            // here would hide people one tap would reach.
            search: `NOT archived:true${scope}`,
            sort: { field: `createdDate`, direction: `DESC` },
            pagination: { limit: COHORT_PAGE, page: index },
          })
          if (result.error) throw result.error
          if (token !== generation.current) return undefined
          const data = result.data?.messages
          return { hits: data?.hits ?? [], total: data?.total ?? 0 }
        }),
        // No field list, so this takes the full model selection. An expanded row
        // draws the real activity card, and those read recordings, durations,
        // notes and the AI summary: everything a narrow list would have to guess
        // at on their behalf.
        // The practice's own opt-out records. Cheap: one key per opted-out
        // number, and the queue needs them before it can say who is waiting.
        gqlFetchList<{ key: string }>(
          `optOuts`,
          {
            search: `type:PHONE${scope}`,
            pagination: { limit: 1000 },
          },
          `key`,
        ),
        gqlFetchList<ActivityJson>(
          `activities`,
          {
            // Bracket ranges, not `>=`. The search rejects `activityDate:>=<iso>` with
            // "Unexpected error", and because a GraphQL error is still an HTTP 200 the
            // failure reads as an empty queue rather than as a fault. Same form the
            // hygiene queues use.
            search: `${REQUEST_TYPES} AND activityDate:[${activitySince} TO *]${scope}`,
            sort: { field: `activityDate`, direction: `DESC` },
            pagination: { limit: 500 },
          },
          // Deliberately narrow. This page is read to decide who is waiting and
          // is never drawn, so the full model over 500 records every thirty
          // seconds would be roughly 800KB of payload to render a handful of
          // rows. The survivors are re-fetched whole below.
          RULE_FIELDS,
        ),
        // The practice's holds on this queue: who was marked done or snoozed,
        // by anybody, on any machine. Failure falls back to undefined rather
        // than failing the load, and undefined keeps the previous answer:
        // wiping it would resurrect every cleared row on one bad request.
        fetchHolds(`queueName:"${RESPONSE_QUEUE_NAME}"`).catch((err) => {
          logWarn(`Could not read the response queue's holds`, err)
          return undefined
        }),
      ])
        .then(async ([messages, optOutResult, activityResult, holds]) => {
          if (token !== generation.current) return
          if (holds !== undefined) {
            setDismissed(holdsToDismissals(holds))
            // The server has answered, so optimistic entries old enough to be
            // reflected in that answer have done their job.
            setOverlay((current) => {
              const cutoff = Date.now() - OVERLAY_GRACE_MS
              return Object.fromEntries(Object.entries(current).filter(([, entry]) => entry.at >= cutoff))
            })
          }
          const hits = activityResult.hits ?? []
          const activities = toQueueActivities(hits)
          const optedOut = new Set((optOutResult.hits ?? []).map((row) => row.key).filter(Boolean))
          const merged = mergeWaiting([
            // The generated hits assign straight into `QueueMessage`: the domain
            // type was written null-tolerant, so the transport needs no mapper.
            // A message that lands mid-read shifts every later page by one and
            // repeats a row at the page edge, which would count a touch twice.
            ...waitingFromMessages([...new Map(messages.map((message) => [message.id, message])).values()]),
            ...waitingFromActivities(activities),
          ])

          const ids = [...new Set(merged.flatMap((item) => (item.contactId ? [item.contactId] : [])))]
          // No field list, so this takes the full model selection. A row opens
          // the contact drawer, which reads far more of the record than the row
          // itself shows, and the focus queue fetches contacts the same way.
          const contacts = ids.length
            ? await gqlFetchList<ContactJson>(`contacts`, {
                search: `id:(${ids.map((id) => `"${id}"`).join(` OR `)})`,
                pagination: { limit: ids.length },
              })
            : { hits: [] as ContactJson[] }
          if (token !== generation.current) return
          const byId = new Map((contacts.hits ?? []).map((contact) => [contact.id, contact]))
          // The touches that survived, fetched whole. The sweep above carries
          // only the fields the rules read, so the cards would have nothing to
          // draw: no recording, no AI summary, no transcript. This is the same
          // shape as the contact fetch above, and it asks for a handful of
          // records rather than the whole page.
          const eventIds = [...new Set(merged.flatMap((item) => item.events.map((event) => event.id)))]
          const full = eventIds.length
            ? await gqlFetchList<ActivityJson>(`activities`, {
                search: `id:(${eventIds.map((id) => `"${id}"`).join(` OR `)})`,
                pagination: { limit: eventIds.length },
              })
            : { hits: [] as ActivityJson[] }
          if (token !== generation.current) return
          const byActivityId = new Map((full.hits ?? []).map((activity) => [activity.id, activity]))
          /**
           * Work a colleague already did, through a surface the rules cannot
           * see.
           *
           * Only owned activities: the feed is overwhelmingly machine-written
           * and those rows are ownerless, so the owner is what separates a
           * person's work from the system writing a row. Any type at all,
           * because the point is to catch the ones the queue does not model, a
           * note or an email or a booked appointment.
           *
           * Bounded by the oldest row's own `latest`, so this reads the window
           * the queue is already working rather than the practice's history.
           */
          const oldestLatest = merged
            .filter((item) => item.contactId)
            .map((item) => item.latest)
            .sort()
            .shift()
          const patientIds = (contacts.hits ?? []).flatMap((contact) => contact.patientIds ?? [])
          const [staffWork, bookings] = await Promise.all([
            ids.length && oldestLatest
              ? gqlFetchList<ActivityJson>(
                  `activities`,
                  {
                    search: `contacts.id.keyword:(${ids.map((id) => `"${id}"`).join(` OR `)}) AND activityDate:[${oldestLatest} TO *]`,
                    sort: { field: `activityDate`, direction: `DESC` },
                    pagination: { limit: 500 },
                  },
                  STAFF_TOUCH_FIELDS,
                )
              : { hits: [] as ActivityJson[] },
            // Same window, read from the appointment records: a booking made in
            // the PMS leaves no owned activity for the sweep above to find.
            patientIds.length && oldestLatest
              ? gqlFetchList<Appointment>(
                  `appointments`,
                  {
                    search: `patientId:(${patientIds.map((id) => `"${id}"`).join(` OR `)}) AND createdDate:[${oldestLatest} TO *]`,
                    pagination: { limit: 500 },
                  },
                  BOOKING_FIELDS,
                )
              : { hits: [] as Appointment[] },
          ])
          if (token !== generation.current) return
          const touches: StaffTouch[] = [
            ...(staffWork.hits ?? []).flatMap((activity) => {
              const at = activity.activityDate ?? activity.createdDate
              const contactId = activity.contacts?.[0]?.id
              // Ownerless is the machine. No date cannot be placed in time.
              if (!activity.owner?.id || !at || !contactId) return []
              return [{ contactId, at, actor: activity.owner.name ?? undefined, type: activity.type ?? undefined }]
            }),
            ...bookingTouches(bookings.hits ?? [], contacts.hits ?? []),
          ]
          setRows(
            merged
              // Somebody who told the practice to stop is not waiting on a
              // reply, and there is no reply to give: the send path refuses
              // every outbound message to that number. Dropped rather than
              // marked, because unlike work a colleague did there is nothing
              // here for anyone to pick up.
              .filter((item) => {
                const contact = item.contactId ? byId.get(item.contactId) : undefined
                return !hasOptedOut(item.phone ?? contact?.cellPhone, optedOut)
              })
              .map((item) => ({
                ...item,
                contact: item.contactId ? byId.get(item.contactId) : undefined,
                handled: handledAfter(item, touches),
                // `events` is already newest last, so the cards come out in the
                // order the touches happened.
                activities: item.events.flatMap((event) => {
                  const activity = byActivityId.get(event.id)
                  return activity ? [activity] : []
                }),
              })),
          )
          setLoading(false)
        })
        .catch((err) => {
          if (token !== generation.current) return
          logError(`Action Center failed to load the response queue`, err)
          setLoading(false)
        }),
    )
  }, [activitySince, locationTerm])

  useEffect(() => load(), [load])

  // Same 30s cadence the inbox uses. A shared queue that never refreshes has
  // everyone working rows their colleagues already cleared.
  useEffect(() => {
    const timer = setInterval(load, 30_000)
    return () => clearInterval(timer)
  }, [load])

  /**
   * Requests from before yesterday, counted but never listed.
   *
   * Only activities have a window now, so only activities can fall behind one.
   * Each of these is somebody who rang or wrote and was never answered, and
   * showing them would make the queue unclearable: they are a decision to ring
   * back, not this morning's work. Saying how many there are is the honest
   * middle, so nobody assumes an empty section means an empty history.
   *
   * The same rules the list runs, rather than a raw `total`. A count of every
   * older `Call` would be mostly calls the practice answered, which would read
   * as a backlog that does not exist.
   */
  useEffect(() => {
    const scope = locationTerm ? ` AND ${locationTerm}` : ``
    bg(
      gqlFetchList<ActivityJson>(
        `activities`,
        {
          search: `${REQUEST_TYPES} AND activityDate:[* TO ${activitySince}]${scope}`,
          sort: { field: `activityDate`, direction: `DESC` },
          // A floor rather than a total: this range runs back to the practice's
          // first ever call, so a page is all we are willing to read for a
          // footnote. Anything answered by a follow-up logged inside the window
          // also counts here, because the record that cleared it is out of range.
          pagination: { limit: 500 },
        },
        RULE_FIELDS,
      )
        .then((result) => {
          const older = waitingFromActivities(toQueueActivities(result.hits ?? []))
          setOlderCount(older.length)
          // The same nouns the sections above use, so the footnote reads as the
          // tail of the same list rather than as a separate tally.
          setOlderSummary(summarise(older))
        })
        .catch(() => {
          setOlderCount(0)
          setOlderSummary(``)
        }),
    )
  }, [activitySince, locationTerm])

  const reply = useCallback(async (row: ResponseRow, text: string) => {
    const to = row.contact?.cellPhone ?? row.phone
    const locationId = row.contact?.locationId ?? row.locationId
    if (!to || !locationId) return false
    const result = await gqlMutation(SEND_SMS_MUTATION, {
      input: {
        to,
        text,
        locationId,
        contactId: row.contactId,
      },
    })
    if (result.error || !result.data?.sendSms?.id) return false
    setCleared((current) => [...current, row.key])
    return true
  }, [])

  /**
   * Record a callback as a real FollowUp activity.
   *
   * Each outcome does something different on purpose. `connected` and
   * `scheduled` answer the person, so both set `isConnected` and earn the
   * backend's `Connected` tag, which is what `isResponse` keys on. Only
   * `scheduled` sets `isScheduled`: without it every callback that booked
   * somebody was written down as `Appointment Not Scheduled`, which understated
   * the practice's own booking rate on their own reporting. `left-message`
   * carries a follow-up date instead: they are still waiting, so the row is
   * deferred rather than cleared. `no-answer` changes nothing about their wait,
   * so the row stays exactly where it is.
   */
  const logCallOutcome = useCallback(
    async (row: ResponseRow, outcome: CallOutcome) => {
      const locationId = row.contact?.locationId
      if (!locationId || !row.contactId) return false
      // Local midnight, so a message left at 4pm takes the row off today's list
      // and puts it back at the start of tomorrow rather than 24 hours later.
      const tomorrow = moment().add(1, `day`).startOf(`day`)
      const result = await gqlMutation(CREATE_FOLLOW_UP_MUTATION, {
        input: {
          contactId: row.contactId,
          locationId,
          description: outcomeDescription(outcome),
          isConnected: isAnswered(outcome),
          isScheduled: outcome === `scheduled`,
          followUpDate: outcome === `left-message` ? tomorrow.toISOString() : undefined,
          ownerId: user?.id,
          ownerName: user?.name,
        },
      })
      if (result.error || !result.data?.createFollowUp?.id) return false
      // The FollowUp is what keeps an answered row cleared, on the next poll
      // and on every reload. Clearing here is only so the row goes at the click
      // rather than up to thirty seconds later, which would invite a second
      // follow-up for the same call.
      if (isAnswered(outcome)) setCleared((current) => [...current, row.key])
      // The follow-up carries the same date the row is hidden to, so the record
      // and the list agree on when this person is due back.
      if (outcome === `left-message`) remember(row, tomorrow.toISOString())
      return true
    },
    [remember, user?.id, user?.name],
  )

  /**
   * Drop the unread mark on a row whose thread has just been read.
   *
   * The mutation goes out from `useContactMessages`, but this hook's own copy
   * of the row would keep the dot until the next poll, so the list would sit
   * there claiming unread for up to thirty seconds after you read it. The row
   * stays: reading is not answering.
   */
  const markRead = useCallback((row: ResponseRow) => {
    setRows((current) =>
      current.map((candidate) => (candidate.id === row.id ? { ...candidate, unread: false } : candidate)),
    )
  }, [])

  const dismiss = useCallback((row: ResponseRow) => remember(row), [remember])

  /**
   * Put someone down for now, not away.
   *
   * Stored in the same place as a dismissal, with an `until` date rather than
   * without one, so the row comes back on its own. It also un-suppresses early
   * if that person reaches out again, which is the behaviour that makes it safe
   * to snooze without feeling like you have dropped somebody.
   */
  const snooze = useCallback((row: ResponseRow, until: Date) => remember(row, until.toISOString()), [remember])

  const visible = useMemo(() => {
    const now = new Date()
    return rows.filter((row) => {
      if (cleared.includes(row.key)) return false
      const record = overlay[row.id]?.record ?? dismissed[row.id]
      return !isDismissed(record, row, now)
    })
  }, [rows, cleared, dismissed, overlay])

  return {
    rows: visible,
    olderCount,
    olderSummary,
    loading,
    reply,
    dismiss,
    snooze,
    markRead,
    logCallOutcome,
    reload: load,
  }
}
